import { useEffect } from 'react';
import { Link, useParams, useLocation } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { getArticleById, articles } from '@/mocks/articles';

export default function ArticleDetailPage() {
  const { id } = useParams<{ id: string }>();
  const location = useLocation();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [location.pathname]);

  const article = getArticleById(id || '');
  const relatedArticles = articles
    .filter((a) => a.id !== id && a.category === article?.category)
    .slice(0, 3);

  if (!article) {
    return (
      <>
        <Navbar />
        <main className="pt-20 min-h-screen flex items-center justify-center">
          <div className="text-center">
            <div className="w-16 h-16 rounded-2xl bg-secondary-100 flex items-center justify-center mx-auto mb-4">
              <i className="ri-error-warning-line text-2xl text-secondary-600"></i>
            </div>
            <h1 className="text-xl font-bold text-foreground-900 mb-2">文章不存在</h1>
            <p className="text-foreground-500 text-sm mb-6">未找到该文章，请检查链接是否正确</p>
            <Link to="/articles" className="text-primary-600 hover:text-primary-700 text-sm font-medium">
              返回文章列表 →
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const paragraphs = article.content.split('\n\n');

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Article",
            "headline": article.title,
            "description": article.excerpt,
            "author": {
              "@type": "Person",
              "name": article.author
            },
            "datePublished": article.publishDate,
            "publisher": {
              "@type": "Organization",
              "name": "一级免费网站免费资源导航平台"
            }
          })
        }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            "itemListElement": [
              {
                "@type": "ListItem",
                "position": 1,
                "name": "首页",
                "item": "https://4dsu.cn/"
              },
              {
                "@type": "ListItem",
                "position": 2,
                "name": "精选文章",
                "item": "https://4dsu.cn/articles"
              },
              {
                "@type": "ListItem",
                "position": 3,
                "name": article.title
              }
            ]
          })
        }}
      />
      <Navbar />
      <main className="pt-20">
        <article className="w-full px-4 md:px-6 py-12 bg-background-50">
          <div className="max-w-[800px] mx-auto">
            <nav className="flex items-center gap-2 text-xs text-foreground-400 mb-8 flex-wrap">
              <Link to="/" className="hover:text-primary-600 transition-colors">首页</Link>
              <i className="ri-arrow-right-s-line"></i>
              <Link to="/articles" className="hover:text-primary-600 transition-colors">精选文章</Link>
              <i className="ri-arrow-right-s-line"></i>
              <span className="text-foreground-600 line-clamp-1">{article.title}</span>
            </nav>

            <header className="mb-10">
              <div className="flex items-center gap-2 mb-4">
                <span className="px-2.5 py-1 bg-accent-50 text-accent-700 text-xs rounded-md font-medium">
                  {article.category}
                </span>
                {article.tags.map((tag) => (
                  <span key={tag} className="text-foreground-400 text-xs">#{tag}</span>
                ))}
              </div>
              <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold text-foreground-900 font-heading leading-tight mb-4">
                {article.title}
              </h1>
              <div className="flex items-center gap-4 text-foreground-400 text-sm">
                <span className="flex items-center gap-1.5">
                  <i className="ri-user-line"></i>
                  {article.author}
                </span>
                <span className="flex items-center gap-1.5">
                  <i className="ri-calendar-line"></i>
                  {article.publishDate}
                </span>
                <span className="flex items-center gap-1.5">
                  <i className="ri-time-line"></i>
                  {article.readTime}
                </span>
              </div>
            </header>

            <div className="prose max-w-none">
              {paragraphs.map((para, idx) => {
                const trimmed = para.trim();
                if (!trimmed) return null;

                if (trimmed.startsWith('## ')) {
                  return (
                    <h2 key={idx} className="text-xl font-bold text-foreground-900 mt-10 mb-4 font-heading">
                      {trimmed.replace('## ', '')}
                    </h2>
                  );
                }

                if (trimmed.startsWith('### ')) {
                  return (
                    <h3 key={idx} className="text-lg font-semibold text-foreground-800 mt-8 mb-3 font-heading">
                      {trimmed.replace('### ', '')}
                    </h3>
                  );
                }

                if (trimmed.startsWith('**') && trimmed.endsWith('**')) {
                  return (
                    <h4 key={idx} className="text-base font-semibold text-foreground-800 mt-6 mb-2 font-heading">
                      {trimmed.replace(/\*\*/g, '')}
                    </h4>
                  );
                }

                if (trimmed.startsWith('- ')) {
                  const items = trimmed.split('\n').filter((l) => l.trim().startsWith('- '));
                  return (
                    <ul key={idx} className="list-disc pl-5 mb-4 space-y-1">
                      {items.map((item, i) => (
                        <li key={i} className="text-foreground-700 text-sm leading-relaxed">
                          {item.replace('- ', '')}
                        </li>
                      ))}
                    </ul>
                  );
                }

                if (/^\d+\.\s/.test(trimmed)) {
                  const items = trimmed.split('\n').filter((l) => /^\d+\.\s/.test(l.trim()));
                  return (
                    <ol key={idx} className="list-decimal pl-5 mb-4 space-y-1">
                      {items.map((item, i) => (
                        <li key={i} className="text-foreground-700 text-sm leading-relaxed">
                          {item.replace(/^\d+\.\s/, '')}
                        </li>
                      ))}
                    </ol>
                  );
                }

                return (
                  <p key={idx} className="text-foreground-700 text-sm leading-relaxed mb-4">
                    {trimmed}
                  </p>
                );
              })}
            </div>

            {relatedArticles.length > 0 && (
              <div className="mt-16 pt-10 border-t border-background-200/70">
                <h3 className="text-lg font-bold text-foreground-900 mb-6 font-heading">
                  相关文章
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  {relatedArticles.map((ra) => (
                    <Link
                      key={ra.id}
                      to={`/article/${ra.id}`}
                      className="group bg-background-50 border border-background-200/70 rounded-xl p-5 hover:border-primary-200 transition-all duration-200"
                    >
                      <h4 className="text-foreground-900 font-semibold text-sm mb-2 group-hover:text-primary-600 transition-colors font-heading line-clamp-2">
                        {ra.title}
                      </h4>
                      <p className="text-foreground-500 text-xs line-clamp-2 mb-3">{ra.excerpt}</p>
                      <span className="text-foreground-400 text-[11px]">{ra.publishDate}</span>
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </div>
        </article>
      </main>
      <Footer />
    </>
  );
}